import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';

interface SubscriptionGuardProps {
  children: React.ReactNode;
  redirectTo?: string;
  allowBillingPage?: boolean; // Allow access to billing page even without subscription
  allowedUserTypes?: string[]; // Allow only specific user types (e.g., ['regular'] to exclude 'shopify_store')
}

/**
 * SubscriptionGuard: Non-blocking subscription check with user type filtering
 * 
 * This component checks if the user has an active subscription but doesn't
 * forcefully redirect. Instead, it allows users to browse the app and shows
 * warnings/banners. Individual pages can check subscription status and show
 * appropriate messages or redirect when users try to perform actions.
 * 
 * Additionally, it can filter access based on user_type in the subscription.
 */
export function SubscriptionGuard({ 
  children, 
  redirectTo = '/home/billing',
  allowBillingPage = true,
  allowedUserTypes
}: SubscriptionGuardProps) {
  const location = useLocation();

  // Check if current route is the billing page
  const isBillingPage = location.pathname === redirectTo || 
                        location.pathname === '/home/billing' ||
                        location.pathname.startsWith('/home/billing');

  // Fetch current subscription (non-blocking). Uses the shared hook so this
  // component + AppShell + AppSidebar + TopNavigation + SubscriptionGuardContext
  // + BillingDialog all share one cached fetch instead of 6 parallel requests.
  const { data: subscription, isLoading } = useCurrentSubscription();

  // Check user type restrictions
  if (!isLoading && allowedUserTypes && subscription) {
    const userType = subscription.user_type || 'regular';
    
    // If user type is not in allowed list, redirect to dashboard
    if (!allowedUserTypes.includes(userType)) {
      return <Navigate to="/home" replace />;
    }
  }

  // Don't redirect - let users see the Dashboard with a warning banner
  // The Dashboard component already shows a subscription warning banner
  // This allows users to browse and see what they're missing without forcing them to billing
  // useEffect(() => {
  //   // Only redirect from dashboard, not from other pages
  //   if (location.pathname === '/home' || location.pathname === '/home/') {
  //     if (!isLoading) {
  //       const hasActiveSubscription = subscription && 
  //         (subscription.status === 'active' || subscription.status === 'trialing');
  //       
  //       if (!hasActiveSubscription && !isBillingPage) {
  //         // Soft redirect only from dashboard
  //         navigate(redirectTo, { replace: true });
  //       }
  //     }
  //   }
  // }, [subscription, isLoading, location.pathname, redirectTo, isBillingPage, navigate]);

  // Always allow access - let individual pages handle subscription checks
  // This allows users to browse and see what they're missing
  return <>{children}</>;
}

